<template>
  <TransactionAssetField
    v-model="modelValue"
    v-bind="$attrs"
    :data
    :error
    :description
    :settings
    @create="handleCreate"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount } from 'vue'

import { TransactionClass, TransactionComputedEntry } from '@types'

import { getAssetListValues, onCreateAsset, preSetAsset } from './utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useModalsStore } from '@/store/modals'

import TransactionAssetField from './TransactionAssetField.vue'

type Props = {
  entryIndex: number
  instance: TransactionClass
}

const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()
const repositoriesStore = useRepositoriesStore()
const modalsStore = useModalsStore()

const entryKey = computed(() => `entry${props.entryIndex + 1}` as keyof TransactionClass)

const modelValue = computed({
  get() {
    return (props.instance[entryKey.value] as TransactionComputedEntry)().value?.asset_id || undefined
  },
  set(value) {
    (props.instance[entryKey.value] as TransactionComputedEntry)().value = {
      ...(props.instance[entryKey.value] as TransactionComputedEntry)().value,
      asset_id: value || null
    }
  },
})

const entrySettings = computed(
  () => props.instance.settings?.entries[props.entryIndex],
)
const settings = computed(() => entrySettings.value?.asset)

const error = computed(
  () => props.instance.errors[`"${entrySettings.value?.title}" asset`],
)

const description = computed(() => entrySettings?.value?.assetDescription)

const assetsList = computed(() =>
  Array.from(assetsBunchStore.getList.values()).map(instance => instance.get()),
)

const currencyID = computed(() => {
  const asset =
    assetsBunchStore.getElementByName(repositoriesStore.getCurrentCurrency) ||
    assetsBunchStore.getElementByTicker(repositoriesStore.getCurrentCurrency)
  return asset?.id
})

const data = computed(() =>
  getAssetListValues(
    settings.value,
    assetsList.value,
    (props.instance[entryKey.value] as TransactionComputedEntry)().value?.account_id,
  ),
)

const handleCreate = (value: string) => {
  const instance = assetsBunchStore.createElement()
  onCreateAsset(
    value,
    settings.value?.includes,
    instance,
    modalsStore,
    (data: string) => {
      modelValue.value = data
    },
  )
}

onBeforeMount(() => {
  if (modelValue.value) return
  modelValue.value =
    preSetAsset(
      modelValue.value,
      settings.value,
      assetsBunchStore,
      currencyID.value,
    ) || ''
})
</script>
